import React from 'react';
import {
  kindLabel,
  reminderError,
  useReminderPreview,
} from '../../hooks/useCohortReminders';
import { CARD, formatDateTime, previewTotals } from './reminderText';

// What the next 72 hours would send under the saved settings, as if
// reminders were on now. Reading it sends nothing.
export default function ReminderPreview({ cohortId }) {
  const { data, isLoading, error, refetch, isFetching } =
    useReminderPreview(cohortId);
  const reminders = data?.reminders ?? [];
  const totals = previewTotals(data);
  const sample = reminders.find((r) => r.sms_text)?.sms_text;

  return (
    <section className={`${CARD} space-y-4 p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Next 72 hours</p>
          <p className="text-xs text-gray-500">
            What would go out under the saved settings if reminders were on now.
            Nothing is sent from here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs text-claret-500 hover:underline disabled:opacity-50"
        >
          {isFetching ? 'Checking…' : 'Check again'}
        </button>
      </div>

      {isLoading && (
        <div
          className="h-24 rounded-lg bg-gray-100 animate-pulse"
          aria-label="Loading"
        />
      )}
      {error && (
        <p className="text-sm text-gray-600">
          {reminderError(error, "We couldn't load the preview.")}
        </p>
      )}
      {data && reminders.length === 0 && (
        <p className="text-sm text-gray-600">
          No assignment deadlines need reminders in the next 72 hours.
        </p>
      )}
      {data && reminders.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['In-app', totals.inApp],
              ['Email', totals.email],
              ['SMS', data.sms_messages ?? totals.sms],
              [
                'SMS cost',
                data.sms_cost_kes !== undefined && data.sms_cost_kes !== null
                  ? `KES ${data.sms_cost_kes}`
                  : '–',
              ],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg bg-gray-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">
                  {label}
                </p>
                <p className="text-lg font-semibold tabular-nums text-gray-900">
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </p>
              </div>
            ))}
          </div>
          {data.sms_segments ? (
            <p className="-mt-2 text-xs text-gray-500">
              {data.sms_segments.toLocaleString()} SMS segments in total.
            </p>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-4 pr-2 font-medium">Sends</th>
                  <th className="px-2 py-2 font-medium">Assignment</th>
                  <th className="px-2 py-2 font-medium">Reminder</th>
                  <th className="px-2 py-2 font-medium">Deadline</th>
                  <th className="py-2 pl-2 pr-4 text-right font-medium">
                    In-app / email / SMS
                  </th>
                </tr>
              </thead>
              <tbody>
                {reminders.map((r) => (
                  <tr
                    key={`${r.assignment_id}-${r.kind}`}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="whitespace-nowrap py-2 pl-4 pr-2 text-gray-700">
                      {formatDateTime(r.sends_at)}
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-gray-900">{r.title}</span>
                      {r.module && (
                        <span className="block text-xs text-gray-500">
                          {r.module}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-gray-700">
                      {kindLabel(r.kind)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-gray-700">
                      {formatDateTime(r.deadline)}
                    </td>
                    <td className="py-2 pl-2 pr-4 text-right tabular-nums text-gray-700">
                      {(r.recipients?.in_app ?? 0).toLocaleString()} /{' '}
                      {(r.recipients?.email ?? 0).toLocaleString()} /{' '}
                      {(r.recipients?.sms ?? 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sample && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Example SMS</p>
              <p className="rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800">
                {sample}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
